import type {
  FlagDefinition,
  FlagDefinitions,
  FlagValues,
  PartialFlagValues,
} from './definitions';
import { loadOverrides, type OverridesOptions } from './overrides';
import type { Stand } from './stand';
import { pickFlagValues } from './values';

export type FeatureFlagsOptions<Name extends string> = {
  /** The stand of this build, selects `enabled[stand]` of every flag. */
  stand: Stand;
  /** Initial runtime values, e.g. a cached remote config or values for a test. */
  runtime?: PartialFlagValues<Name>;
  /** Manual overrides from devtools, persisted in storage. Off by default. */
  overrides?: boolean | OverridesOptions;
};

/** Sources of flag values, highest priority first. */
export type FlagSource = 'override' | 'runtime' | 'stand';

export type FlagLayers<Name extends string> = Readonly<{
  override: PartialFlagValues<Name>;
  runtime: PartialFlagValues<Name>;
  stand: FlagValues<Name>;
}>;

// Method signatures are bivariant in their parameters: the store of an app's flags is assigned
// to FeatureFlags<string> in the provider and devtools.
/* oxlint-disable typescript/method-signature-style */
export type FeatureFlagsOverrides<Name extends string> = {
  /** Sets a manual value, `undefined` removes it. Persisted in storage. */
  set(name: Name, value: boolean | undefined): void;
  reset(): void;
};

export type FeatureFlags<Name extends string> = {
  readonly stand: Stand;
  readonly names: readonly Name[];
  readonly definitions: Readonly<Record<Name, FlagDefinition>>;
  /** Null unless overrides are enabled. */
  readonly overrides: FeatureFlagsOverrides<Name> | null;
  isEnabled(name: Name): boolean;
  getSnapshot(): FlagValues<Name>;
  /** Values of every source, for tooling. A new object whenever any source changes. */
  getLayers(): FlagLayers<Name>;
  subscribe(listener: () => void): () => void;
  /**
   * Replaces runtime values, e.g. with a freshly fetched remote config.
   * Unknown names and non-boolean values are ignored.
   */
  setRuntime(values: Readonly<Record<string, unknown>>): void;
};

/* oxlint-enable typescript/method-signature-style */

const SOURCES: readonly FlagSource[] = ['override', 'runtime', 'stand'];

type Values = Readonly<Record<string, boolean | undefined>>;

function shallowEqual(a: Values, b: Values) {
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every(key => a[key] === b[key])
  );
}

/** The source the current value of `name` comes from. */
export function resolveFlagSource<Name extends string>(
  layers: FlagLayers<Name>,
  name: Name
): FlagSource {
  return SOURCES.find(source => layers[source][name] !== undefined) ?? 'stand';
}

/** Priority, highest first: overrides, runtime, the value of the stand. */
export function createFeatureFlags<const T extends FlagDefinitions>(
  definitions: T,
  options: FeatureFlagsOptions<keyof T & string>
): FeatureFlags<keyof T & string> {
  type Name = keyof T & string;

  const names = Object.keys(definitions) as Name[];
  const standValues = Object.fromEntries(
    names.map(name => [name, definitions[name].enabled[options.stand]])
  ) as FlagValues<Name>;
  const storedOverrides = options.overrides
    ? loadOverrides(names, options.overrides === true ? {} : options.overrides)
    : null;
  const listeners = new Set<() => void>();

  let layers: FlagLayers<Name> = {
    override: storedOverrides?.values ?? {},
    runtime: options.runtime ? pickFlagValues(names, options.runtime) : {},
    stand: standValues,
  };
  let snapshot = compute();

  function compute(): FlagValues<Name> {
    return { ...layers.stand, ...layers.runtime, ...layers.override };
  }

  // Keeps the snapshot when values stay the same, so flag consumers do not re-render.
  function update(patch: Partial<FlagLayers<Name>>) {
    layers = { ...layers, ...patch };

    const next = compute();
    if (!shallowEqual(next, snapshot)) {
      snapshot = next;
    }

    for (const listener of listeners) {
      listener();
    }
  }

  function setOverrides(next: PartialFlagValues<Name>) {
    if (!storedOverrides || shallowEqual(next, layers.override)) {
      return;
    }

    storedOverrides.save(next);
    update({ override: next });
  }

  return {
    stand: options.stand,
    names,
    definitions,
    overrides: storedOverrides && {
      set: (name, value) => {
        const next = { ...layers.override };
        if (value === undefined) {
          delete next[name];
        } else {
          next[name] = value;
        }
        setOverrides(next);
      },
      reset: () => {
        setOverrides({});
      },
    },
    isEnabled: name => snapshot[name] === true,
    getSnapshot: () => snapshot,
    getLayers: () => layers,
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setRuntime: values => {
      const next = pickFlagValues(names, values);
      if (!shallowEqual(next, layers.runtime)) {
        update({ runtime: next });
      }
    },
  };
}
